/**
 * A linha que se digita fica ACIMA do teclado virtual — a conta, sem DOM (patch 0027 do Mirezi).
 *
 * Card 1879847269969167640. No iPhone, escrevendo no fim de um documento, a linha ia parar atrás
 * do teclado à medida que o texto crescia. A rolagem automática do `rich-text` comparava a
 * posição do cursor com a caixa do contêiner de rolagem — e o teclado NÃO encolhe essa caixa: ele
 * encolhe o *visual viewport*; o layout viewport (de onde saem `getBoundingClientRect` e `100dvh`)
 * fica igual. O tratamento de teclado do Affine existe, mas só no bundle mobile, que o Mirezi não
 * serve ao iPhone (patch 0002).
 *
 * Mesma forma do publicador de teclado do mural (`apps/mural/client/src/lib/altura-do-teclado.js`):
 *  - **piso de 150px** — a barra de endereço do Safari já faz a área visível ser 44–88px menor
 *    que a janela SEM teclado nenhum; abaixo do piso, "não há teclado";
 *  - o **fundo visível** é `offsetTop + height` do visual viewport — o iOS também DESLIZA a área
 *    visível (card 1872916253903947430), e usar só a altura erraria pelo deslize.
 *
 * Arquivo NOSSO (declarado em `infra/affine/UPSTREAM`), sem DOM, executado por
 * `cli/tests/js/teclado-visivel.test.ts`.
 */

export const PISO_DO_TECLADO = 150;
export const FOLGA_ACIMA_DO_TECLADO = 16;

/** Quanto o teclado toma da janela — 0 abaixo do piso (é barra de endereço, não teclado). */
export function alturaDoTeclado(alturaDaJanela: number, alturaVisivel: number): number {
  const tomada = alturaDaJanela - alturaVisivel;
  return Number.isFinite(tomada) && tomada >= PISO_DO_TECLADO ? tomada : 0;
}

export interface Visivel {
  /** há teclado aberto (acima do piso) */
  teclado: boolean;
  /** o fundo da área visível, nas coordenadas do `getBoundingClientRect` */
  fundo: number;
}

/** A área visível agora — `null` onde não há `visualViewport` (nada a fazer). */
export function areaVisivel(
  janela: { innerHeight: number; visualViewport?: { height: number; offsetTop: number } | null }
): Visivel | null {
  const vv = janela.visualViewport;
  if (!vv) return null;
  return {
    teclado: alturaDoTeclado(janela.innerHeight, vv.height) > 0,
    fundo: vv.offsetTop + vv.height,
  };
}

/**
 * Quanto rolar para a linha ficar visível acima do teclado — 0 se já está.
 *
 * O limite é o MENOR entre o fundo do contêiner e o fundo da área visível, menos uma folga para
 * a linha não encostar no teclado.
 */
export function quantoRolarParaVer(o: {
  fundoDaLinha: number;
  fundoDoConteiner: number;
  fundoVisivel: number;
  folga?: number;
}): number {
  const limite = Math.min(o.fundoDoConteiner, o.fundoVisivel) - (o.folga ?? FOLGA_ACIMA_DO_TECLADO);
  return o.fundoDaLinha > limite ? Math.ceil(o.fundoDaLinha - limite) : 0;
}
